<template>
  <client-only>
    <template v-for="key in activeModalsList" :key="key">
      <Teleport to="body">
        <div class="modal open">
          <div
              v-if="activeModalsList.length > 0"
              :style="{ zIndex: getOverlayZIndex(key) }"
              class="modal__bg"
              @click.self="closeModal(key)"
          />
          <div :id="key" :style="{ zIndex: getModalZIndex(key) }" class="modal__wrap">
            <component
                :is="modals[key as keyof typeof modals]"
                v-if="modals[key as keyof typeof modals]"
            />
          </div>
        </div>
      </Teleport>
    </template>
  </client-only>
</template>

<script lang="ts" setup>
import {
  LazyModalsAddonsApiRequests,
  LazyModalsAddonsMarketOrderAnalytics,
  LazyModalsAddonsMaxOrders,
  LazyModalsAddonsMinPrices,
  LazyModalsAddonsPriceRecommendations,
  LazyModalsAddonsSteamAnalytics,
  LazyModalsComparisonSort,
  LazyModalsComponentsAdvancedSearch,
  LazyModalsComponentsBlogCategories,
  LazyModalsComponentsBuyTokens,
  LazyModalsComponentsCancelSubscription,
  LazyModalsComponentsComparisonEditPreset,
  LazyModalsComponentsComparisonPreset,
  LazyModalsComponentsConfirm,
  LazyModalsComponentsInventoryPrivate,
  LazyModalsComponentsMobileSearch,
  LazyModalsComponentsNavMobile,
  LazyModalsComponentsPaymentMethod,
  LazyModalsComponentsUpgradePlan
} from "#components";
import {ModalIds} from "~/types/Modals";
import {useModalStore} from "~/store/modal";

const modals = {
  [ModalIds.ADVANCED_SEARCH]: markRaw(LazyModalsComponentsAdvancedSearch),
  [ModalIds.CONFIRM]: markRaw(LazyModalsComponentsConfirm),
  [ModalIds.BLOG_CATEGORIES]: markRaw(LazyModalsComponentsBlogCategories),
  [ModalIds.COMPARISON_SORT]: markRaw(LazyModalsComparisonSort),
  [ModalIds.INVENTORY_PRIVATE]: markRaw(LazyModalsComponentsInventoryPrivate),
  [ModalIds.MOBILE_SEARCH]: markRaw(LazyModalsComponentsMobileSearch),
  [ModalIds.MOBILE_NAV]: markRaw(LazyModalsComponentsNavMobile),
  [ModalIds.ADDONS_MAX_ORDERS]: markRaw(LazyModalsAddonsMaxOrders),
  [ModalIds.ADDONS_MARKET_ORDER_ANALYTICS]: markRaw(LazyModalsAddonsMarketOrderAnalytics),
  [ModalIds.ADDONS_STEAM_ANALYTICS]: markRaw(LazyModalsAddonsSteamAnalytics),
  [ModalIds.ADDONS_PRICE_RECOMMENDATIONS]: markRaw(LazyModalsAddonsPriceRecommendations),
  [ModalIds.ADDONS_MIN_PRICES]: markRaw(LazyModalsAddonsMinPrices),
  [ModalIds.ADDONS_ADDITIONAL_API_REQUESTS]: markRaw(LazyModalsAddonsApiRequests),
  [ModalIds.BUY_TOKENS]: markRaw(LazyModalsComponentsBuyTokens),
  [ModalIds.CANCEL_SUBSCRIPTION]: markRaw(LazyModalsComponentsCancelSubscription),
  [ModalIds.PAYMENT_METHOD]: markRaw(LazyModalsComponentsPaymentMethod),
  [ModalIds.UPGRADE_PLAN]: markRaw(LazyModalsComponentsUpgradePlan),
  [ModalIds.COMPARISON_PRESET]: markRaw(LazyModalsComponentsComparisonPreset),
  [ModalIds.COMPARISON_PRESET_EDIT]: markRaw(LazyModalsComponentsComparisonEditPreset),
};

const {activeModals, getModalZIndex, getOverlayZIndex, closeModal} = useModalStore();

const activeModalsList = computed<ModalIds[]>(() =>
    Array.from(activeModals).filter((key) => Object.keys(modals).includes(key))
);
</script>

<style lang="scss">
@use "sass:color";

.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 200;
  opacity: 0;
  visibility: hidden;
  transition: 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;

  &__bg {
    background: color-mix(in srgb, var(--accent-on-primary) 80%, transparent);
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: inherit;
  }

  &__wrap {
    padding: 10px;
    margin: auto;
    overflow-y: auto;
  }

  &__inner {
    background: var(--surfaces-container);
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    max-width: 600px;
    margin: 0 auto;
    width: 100%;
    position: relative;
    z-index: inherit;

    .desc {
      font-weight: 300;
      font-size: 1.2rem;
      line-height: 180%;
      color: var(--surfaces-on-surface-variable);
      margin: 2.4rem 0 1.6rem;

      &.mt10 {
        margin-top: 10px;
      }
    }
  }

  &.open {
    opacity: 1;
    visibility: visible;

    & ~ .app_main,
    & ~ .main-header,
    & ~ .main-footer {
      padding-right: 3px;
      filter: blur(2px);
      @media screen and (max-width: 979px) {
        padding-right: 0;
        filter: unset;
      }
    }
  }

  &_close {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    padding: 0 0 0.5rem;
    min-width: inherit;
    color: var(--surfaces-on-surface-variable);

    &:hover {
      color: var(--surfaces-on-surface);
    }
  }

  &-header {
    padding: 0 2.4rem;
    position: relative;
    height: 7rem;
    display: flex;
    align-items: center;

    &__wrap {
      height: 100%;
      padding: 0.5rem 0;
      width: 100%;
      border-bottom: 1px solid var(--surfaces-high-container);
    }

    h2 {
      font-size: 1.7rem;
      width: 100%;
    }

    &.with-icon {
      .modal-header__wrap {
        flex-wrap: wrap;
      }

      i.btn-primary {
        display: none;
      }
    }
  }

  &-body {
    padding: 0 2.4rem;
  }

  &-footer {
    display: flex;
    align-items: center;
    padding: 1.6rem 2.4rem 2.4rem;
    font-size: 1.4rem;

    .f {
      flex-wrap: wrap;
    }

    .cont {
      padding-left: 2.4rem;
      padding-right: 2.4rem;
    }
  }
}
</style>
